(() => {
  const roots = document.querySelectorAll('[data-bst-wholesale]');

  roots.forEach((root) => {
    const preview = root.dataset.preview === 'true';
    const search = root.querySelector('[data-bst-search]');
    const categoryButtons = Array.from(root.querySelectorAll('[data-bst-category]'));
    const products = Array.from(root.querySelectorAll('[data-bst-product]'));
    const quantityInputs = Array.from(root.querySelectorAll('[data-bst-quantity]'));
    const totalUnits = root.querySelector('[data-bst-total-units]');
    const totalValue = root.querySelector('[data-bst-total-value]');
    const empty = root.querySelector('[data-bst-empty]');
    const submit = root.querySelector('[data-bst-submit]');
    const clear = root.querySelector('[data-bst-clear]');
    const notice = root.querySelector('[data-bst-notice]');
    const currency = root.dataset.currency || 'AUD';
    let activeCategory = 'all';

    const money = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    };

    const normalise = (value) => (value || '').toLowerCase().trim();

    const updateFilters = () => {
      const term = normalise(search?.value);
      let visible = 0;

      products.forEach((product) => {
        const matchesCategory =
          activeCategory === 'all' || product.dataset.category === activeCategory;
        const matchesSearch = !term || normalise(product.dataset.search).includes(term);
        const show = matchesCategory && matchesSearch;
        product.hidden = !show;
        if (term && show) product.open = true;
        if (show) visible += 1;
      });

      empty?.classList.toggle('is-visible', visible === 0);
    };

    const updateTotals = () => {
      let units = 0;
      let cents = 0;

      quantityInputs.forEach((input) => {
        const quantity = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
        units += quantity;
        cents += quantity * Number.parseInt(input.dataset.price || '0', 10);
      });

      if (totalUnits) totalUnits.textContent = String(units);
      if (totalValue) totalValue.textContent = money(cents);
      if (submit) submit.disabled = units === 0 || preview;
    };

    const showNotice = (message, type = 'info') => {
      if (!notice) return;
      notice.textContent = message;
      notice.dataset.type = type;
      notice.classList.add('is-visible');
      notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    search?.addEventListener('input', updateFilters);

    categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeCategory = button.dataset.bstCategory || 'all';
        categoryButtons.forEach((item) => {
          item.classList.toggle('is-active', item === button);
          item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
        });
        updateFilters();
      });
    });

    root.addEventListener('click', (event) => {
      const quantityButton = event.target.closest('[data-bst-qty-action]');
      if (!quantityButton) return;

      const wrapper = quantityButton.closest('[data-bst-variant]');
      const input = wrapper?.querySelector('[data-bst-quantity]');
      if (!input) return;

      const increment = Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
      const minimum = Math.max(0, Number.parseInt(input.min || '0', 10) || 0);
      const current = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
      const next =
        quantityButton.dataset.bstQtyAction === 'increase'
          ? current + increment
          : Math.max(minimum, current - increment);

      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    quantityInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const increment = Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
        const minimum = Math.max(1, Number.parseInt(input.dataset.minimum || '1', 10) || 1);
        let value = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
        if (value > 0 && value < minimum) value = minimum;
        if (value > 0 && value % increment !== 0) {
          value = Math.ceil(value / increment) * increment;
        }
        input.value = String(value);
        updateTotals();
      });
    });

    clear?.addEventListener('click', () => {
      quantityInputs.forEach((input) => {
        input.value = '0';
      });
      updateTotals();
    });

    submit?.addEventListener('click', async () => {
      if (preview) {
        showNotice('Ordering is disabled on the client preview. Approved accounts can submit this order directly.');
        return;
      }

      const items = quantityInputs
        .map((input) => ({
          id: Number.parseInt(input.dataset.variantId, 10),
          quantity: Math.max(0, Number.parseInt(input.value || '0', 10) || 0),
        }))
        .filter((item) => item.id && item.quantity > 0);

      if (!items.length) {
        showNotice('Add at least one item before continuing.');
        return;
      }

      const po = root.querySelector('[data-bst-po]')?.value?.trim() || '';
      const requestedDate = root.querySelector('[data-bst-date]')?.value || '';
      const instructions = root.querySelector('[data-bst-instructions]')?.value?.trim() || '';
      const originalText = submit.textContent;
      submit.disabled = true;
      submit.textContent = 'Preparing order…';

      try {
        const batchSize = 50;
        for (let index = 0; index < items.length; index += batchSize) {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items.slice(index, index + batchSize) }),
          });

          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || 'One or more products could not be added.');
          }
        }

        const attributes = {
          'Wholesale order': 'Yes',
          'Purchase order number': po,
          'Requested delivery date': requestedDate,
        };

        const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes, note: instructions }),
        });

        if (!updateResponse.ok) throw new Error('The order details could not be saved.');
        window.location.assign(`${window.Shopify.routes.root}cart`);
      } catch (error) {
        submit.disabled = false;
        submit.textContent = originalText;
        showNotice(error.message || 'We could not prepare the order. Please try again.', 'error');
      }
    });

    updateFilters();
    updateTotals();
  });
})();
